import React, { useState, useRef } from 'react';
import { gql } from '@apollo/client';
import { 
  Bars3Icon, 
  PlusIcon, 
  TrashIcon, 
  PencilIcon, 
  DocumentTextIcon, 
  DocumentIcon, 
  VideoCameraIcon, 
  CodeBracketIcon, 
  XMarkIcon, 
  ArrowUpTrayIcon, 
  CheckCircleIcon 
} from '@heroicons/react/24/outline';
import client from '../../lib/apolloClient';

const CREATE_DOCUMENT = gql`
  mutation CreateDocument($file: File!, $name: String, $category: String, $document_type: Int) {
    createDocument(file: $file, name: $name, category: $category, document_type: $document_type) {
      id
      name
      download_url
      created_at
    }
  }
`;

const EducationalContentManager = ({ content = [], onChange, className = '' }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isDialogOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isDialogOpen]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    locale: 'EN',
    type: 'markdown',
    content: '',
    url: '',
    fileName: '',
    uploadedFile: null,
    documentId: null,
    isUploading: false,
  });
  const fileInputRef = useRef(null);

  const CONTENT_TYPE_ICONS = {
    markdown: DocumentTextIcon,
    document: DocumentIcon,
    video: VideoCameraIcon,
    iframe: CodeBracketIcon,
  };

  const CONTENT_TYPE_LABELS = {
    markdown: 'Markdown',
    document: 'Document (PDF/Word)',
    video: 'Video',
    iframe: 'iFrame Embed',
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      locale: 'EN',
      type: 'markdown',
      content: '',
      url: '',
      fileName: '',
      uploadedFile: null,
      documentId: null,
      isUploading: false,
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      locale: item.locale || 'EN',
      type: item.content_type || 'markdown',
      content: item.md_body || item.embed_code || '',
      url: item.asset_url || '',
      fileName: item.fileName || '',
      uploadedFile: null,
      documentId: item.documentId || null,
      isUploading: false,
    });
    setIsDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!validTypes.includes(file.type)) {
      alert('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      alert('File size must be less than 10MB');
      return;
    }

    setFormData({ ...formData, isUploading: true });

    try {
      const result = await client.mutate({
        mutation: CREATE_DOCUMENT,
        variables: {
          file: [file],
          name: file.name,
          category: 'other',
          document_type: 0,
        },
        context: { hasUpload: true },
      });

      if (result.data?.createDocument) {
        const document = result.data.createDocument;
        setFormData({
          ...formData,
          uploadedFile: file,
          url: document.download_url,
          fileName: file.name,
          documentId: document.id,
          isUploading: false,
        });
      } else {
        throw new Error('Failed to upload file');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file. Please try again.');
      setFormData({ ...formData, isUploading: false });
    }
  };

  const handleSave = () => {
    if (!formData.title.trim()) return;

    // Validate document type has either URL or uploaded file
    if (formData.type === 'document' && !formData.url && !formData.uploadedFile) {
      alert('Please upload a document or provide a URL');
      return;
    }

    const now = new Date().toISOString();

    if (editingId) {
      // Update existing
      const updated = content.map((item) =>
        item.id === editingId
          ? {
              ...item,
              title: formData.title,
              description: formData.description,
              locale: formData.locale,
              content_type: formData.type,
              content: formData.type === 'markdown' || formData.type === 'iframe' ? formData.content : undefined,
              url: formData.type === 'document' || formData.type === 'video' ? formData.url : undefined,
              fileName: formData.type === 'document' ? formData.fileName : undefined,
              documentId: formData.type === 'document' ? formData.documentId : undefined,
              md_body: formData.type === 'markdown' ? formData.content : undefined,
              embed_code: formData.type === 'iframe' ? formData.content : undefined,
              asset_url: formData.type === 'document' || formData.type === 'video' ? formData.url : undefined,
              updatedAt: now,
            }
          : item
      );
      onChange(updated);
    } else {
      // Add new
      const newItem = {
        id: `edu_${Date.now()}`,
        title: formData.title,
        description: formData.description,
        locale: formData.locale,
        content_type: formData.type,
        content: formData.type === 'markdown' || formData.type === 'iframe' ? formData.content : undefined,
        url: formData.type === 'document' || formData.type === 'video' ? formData.url : undefined,
        fileName: formData.type === 'document' ? formData.fileName : undefined,
        documentId: formData.type === 'document' ? formData.documentId : undefined,
        md_body: formData.type === 'markdown' ? formData.content : undefined,
        embed_code: formData.type === 'iframe' ? formData.content : undefined,
        asset_url: formData.type === 'document' || formData.type === 'video' ? formData.url : undefined,
        sortOrder: content.length,
        createdAt: now,
        updatedAt: now,
      };
      onChange([...content, newItem]);
    }

    setIsDialogOpen(false);
  };

  const handleDelete = (id) => {
    const filtered = content.filter((item) => item.id !== id);
    // Reorder after deletion
    const reordered = filtered.map((item, index) => ({
      ...item,
      sortOrder: index,
    }));
    onChange(reordered);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const items = [...content];
    const draggedItem = items[draggedIndex];
    items.splice(draggedIndex, 1);
    items.splice(index, 0, draggedItem);

    // Update sort order
    const reordered = items.map((item, idx) => ({
      ...item,
      sortOrder: idx,
    }));

    onChange(reordered);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const sortedContent = [...content].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Educational Content</h3>
          <p className="text-xs text-muted-foreground">Add learning materials for participants</p>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] flex items-center gap-2"
        >
          <PlusIcon className="w-4 h-4" />
          Add Content
        </button>
      </div>

      {sortedContent.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-4">No educational content yet</p>
          <button
            type="button"
            onClick={handleAdd}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] flex items-center gap-2 mx-auto"
          >
            <PlusIcon className="w-4 h-4" />
            Add Your First Content
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedContent.map((item, index) => {
            const Icon = CONTENT_TYPE_ICONS[item.content_type] || DocumentTextIcon;
            return (
              <div
                key={item.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 bg-card border border-border rounded-lg hover:bg-accent/50 transition-colors cursor-move ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
              >
                <Bars3Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground flex-shrink-0">
                      {item.locale || 'EN'}
                    </span>
                  </div>
                  {item.description && (
                    <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className="p-1 hover:bg-accent rounded focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    className="p-1 hover:bg-destructive/10 rounded text-destructive focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      {isDialogOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsDialogOpen(false)}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-white border border-border rounded-lg shadow-lg max-w-2xl w-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          >
            {/* Fixed Header */}
            <div className="p-6 border-b border-border flex-shrink-0">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit' : 'Add'} Educational Content</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Provide learning materials to help participants complete this task
              </p>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
              {/* Title */}
              <div className="space-y-2">
                <label htmlFor="title" className="block text-sm font-medium">
                  Title <span className="text-destructive">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., How to Calculate EBITDA"
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label htmlFor="description" className="block text-sm font-medium">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this content"
                  rows={2}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                />
              </div>

              {/* Locale and Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="locale" className="block text-sm font-medium">Language</label>
                  <select
                    id="locale"
                    value={formData.locale}
                    onChange={(e) => setFormData({ ...formData, locale: e.target.value })}
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  >
                    <option value="EN">English</option>
                    <option value="FR">French</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="type" className="block text-sm font-medium">Content Type</label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  >
                    {Object.entries(CONTENT_TYPE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Content-specific fields */}
              {formData.type === 'markdown' && (
                <div className="space-y-2">
                  <label htmlFor="content" className="block text-sm font-medium">Markdown Content</label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter markdown content..."
                    rows={8}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  />
                  <p className="text-xs text-muted-foreground">Supports standard markdown formatting</p>
                </div>
              )}

              {formData.type === 'document' && (
                <div className="space-y-4">
                  {/* File Upload Section */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Upload Document</label>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileUpload}
                        className="hidden"
                      />

                      {formData.uploadedFile ? (
                        <div className="space-y-3">
                          <CheckCircleIcon className="w-12 h-12 mx-auto text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-foreground">{formData.uploadedFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(formData.uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            className="rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                          >
                            Change File
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <ArrowUpTrayIcon className="w-12 h-12 mx-auto text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium text-foreground mb-1">Upload PDF or Word Document</p>
                            <p className="text-xs text-muted-foreground">Click to browse or drag and drop</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={formData.isUploading}
                            className="rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] disabled:opacity-50"
                          >
                            {formData.isUploading ? 'Uploading...' : 'Choose File'}
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Accepted formats: PDF, DOC, DOCX (Max 10MB)</p>
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or provide URL</span>
                    </div>
                  </div>

                  {/* URL Input Section */}
                  <div className="space-y-2">
                    <label htmlFor="url" className="block text-sm font-medium">Document URL</label>
                    <input
                      id="url"
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://example.com/document.pdf"
                      disabled={!!formData.uploadedFile}
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] disabled:opacity-50"
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.uploadedFile ? 'Remove uploaded file to use URL' : 'Link to an existing document'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="fileName" className="block text-sm font-medium">Display Name (optional)</label>
                    <input
                      id="fileName"
                      type="text"
                      value={formData.fileName}
                      onChange={(e) => setFormData({ ...formData, fileName: e.target.value })}
                      placeholder="document.pdf"
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                    />
                    <p className="text-xs text-muted-foreground">Custom name to display (auto-filled from upload)</p>
                  </div>
                </div>
              )}

              {formData.type === 'video' && (
                <div className="space-y-2">
                  <label htmlFor="url" className="block text-sm font-medium">Video URL</label>
                  <input
                    id="url"
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full rounded-lg border border-border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  />
                  <p className="text-xs text-muted-foreground">Supports YouTube, Vimeo, and direct video URLs</p>
                </div>
              )}

              {formData.type === 'iframe' && (
                <div className="space-y-2">
                  <label htmlFor="content" className="block text-sm font-medium">iFrame Embed Code</label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder='<iframe src="..." width="100%" height="400"></iframe>'
                    rows={6}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  />
                  <p className="text-xs text-muted-foreground">Paste the complete iframe embed code</p>
                </div>
              )}
            </div>

            {/* Fixed Footer */}
            <div className="p-6 border-t border-border flex justify-end gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-lg border border-border bg-card px-4 py-2 hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!formData.title.trim()}
                className="rounded-lg bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] disabled:opacity-50"
              >
                {editingId ? 'Save Changes' : 'Add Content'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationalContentManager;

